import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  styled,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import PrintIcon from "@mui/icons-material/Print";
import { QRCodeCanvas } from "qrcode.react";
import { useState } from "react";
import { useMenuIdFromURL } from "../../../hooks/menuIdFromURL";
import { useMenuData } from "../../../menu-provider/MenuDataProvider";

const MenuTab = styled(Tab)({
  border: "1px solid #9b9b9b8c",
  marginRight: "8px",
});

const HowItWorksStep = ({
  number,
  icon,
  text,
}: {
  number: number;
  icon: React.ReactNode;
  text: string;
}) => (
  <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        bgcolor: "primary.main",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: "0.75rem",
        fontWeight: 700,
      }}
    >
      {number}
    </Box>
    <Box color="text.secondary" display="flex" alignItems="center" mr={0.25}>
      {icon}
    </Box>
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Box>
);

export const QRGeneration = () => {
  const menuId = useMenuIdFromURL();
  const { restaurantName } = useMenuData();
  const [selectedMenuOption, setSelectedMenuOption] = useState(QROptions[0]);
  const [tableNameInput, setTableNameInput] = useState("");
  const [copied, setCopied] = useState(false);

  const updateTableName = (newValue: string) => {
    const cleanedValue = newValue.split(" ").join("");
    setTableNameInput(cleanedValue);
  };

  const menuUrl = generateMenuUrl(tableNameInput, menuId);
  const isCustomWithoutName =
    selectedMenuOption.label === QRLabel.Custom && tableNameInput.trim() === "";

  const downloadQR = () => {
    const canvas = document.getElementById("qr-code-id") as HTMLCanvasElement;
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const downloadLink = document.createElement("a");
    downloadLink.href = pngUrl;
    downloadLink.download = `${tableNameInput || "menu"}-qr.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const printCard = () => {
    const canvas = document.getElementById("qr-code-id") as HTMLCanvasElement;
    const qrDataUrl = canvas.toDataURL("image/png");
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(
      buildPrintCardHtml(restaurantName, qrDataUrl, tableNameInput)
    );
    printWindow.document.close();
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(menuUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Paper
      variant="outlined"
      sx={{ padding: "24px", margin: "8px", boxSizing: "border-box" }}
    >
      <Box display="flex" gap={4} alignItems="flex-start">
        <Box flexShrink={0} width={280}>
          <Box
            border="3px solid"
            borderColor="primary.main"
            borderRadius={2}
            overflow="hidden"
            bgcolor="white"
          >
            <Box bgcolor="primary.main" py={2} px={2} textAlign="center">
              <Typography
                variant="subtitle1"
                fontWeight={700}
                color="white"
                noWrap
              >
                {restaurantName || "Your Restaurant"}
              </Typography>
            </Box>

            <Box py={3} px={1} textAlign="center">
              <QRCodeCanvas
                id="qr-code-id"
                value={menuUrl}
                size={220}
                level={"H"}
                includeMargin={false}
                data-testid="test-id-qr-code"
              />
            </Box>

            <Box px={2} pb={3} textAlign="center">
              <Typography variant="body1" fontWeight={700} gutterBottom>
                Scan to view our menu
              </Typography>
              {tableNameInput && (
                <Chip label={tableNameInput} size="small" sx={{ mb: 1 }} />
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                No app needed · Use your phone camera
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            textAlign="center"
            mt={1}
          >
            Preview of your printable card
          </Typography>
        </Box>

        <Box flex={1} minWidth={0}>
          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            QR code type
          </Typography>

          <Tabs
            value={selectedMenuOption}
            onChange={(_, newValue) => {
              setSelectedMenuOption(newValue);
              setTableNameInput(newValue.tableNameType);
            }}
            sx={{ mb: 2 }}
            variant="scrollable"
          >
            {QROptions.map((value, index) => (
              <MenuTab label={value.label} value={value} key={index} />
            ))}
          </Tabs>

          {selectedMenuOption.label === QRLabel.Custom && (
            <TextField
              value={tableNameInput}
              onChange={(event) => updateTableName(event.target.value)}
              placeholder="e.g. Outdoor-1, Table-5"
              size="small"
              required
              sx={{ mb: 2, width: "100%" }}
            />
          )}

          <Typography variant="body2" color="text.secondary" mb={2}>
            {selectedMenuOption.text}
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            gap={1}
            bgcolor="grey.50"
            border="1px solid"
            borderColor="divider"
            borderRadius={1}
            px={1.5}
            py={0.75}
            mb={3}
          >
            <Typography
              component="a"
              href={menuUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              color="primary.main"
              sx={{
                textDecoration: "none",
                flexGrow: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {menuUrl}
            </Typography>
            <Tooltip title={copied ? "Copied!" : "Copy link"}>
              <IconButton size="small" onClick={copyUrl}>
                {copied ? (
                  <CheckIcon fontSize="small" color="success" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" color="text.secondary" mb={1.5}>
            How customers use this
          </Typography>

          <HowItWorksStep
            number={1}
            icon={<PrintIcon fontSize="small" />}
            text="Print this card and place it on your tables or counter"
          />
          <HowItWorksStep
            number={2}
            icon={<PhoneAndroidIcon fontSize="small" />}
            text="Customers scan it with their phone camera — no app needed"
          />
          <HowItWorksStep
            number={3}
            icon={<MenuBookIcon fontSize="small" />}
            text="They see your menu, browse items, and place their order"
          />

          <Box display="flex" gap={1.5} mt={2} flexWrap="wrap">
            <Button
              variant="outlined"
              onClick={printCard}
              disabled={isCustomWithoutName}
              startIcon={<PrintIcon />}
            >
              Print card
            </Button>
            <Button
              variant="contained"
              onClick={downloadQR}
              disabled={isCustomWithoutName}
            >
              Download QR Code
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

const QROptions = [
  {
    label: "Base menu",
    text: "Browse-only link. Customers can view your menu but cannot place orders or call for assistance.",
    tableNameType: "",
  },
  {
    label: "Pick-up",
    text: "For pick-up orders. Place at your counter so customers can browse and order for collection.",
    tableNameType: "Pick-up",
  },
  {
    label: "Delivery",
    text: "For delivery orders. Share with customers so they can browse your menu and order for delivery.",
    tableNameType: "Delivery",
  },
  {
    label: "Custom",
    text: "Custom label (e.g. 'Outdoor-1'). Great for outdoor seating — lets you identify exactly which table or area each order comes from.",
    tableNameType: "",
  },
];

const generateMenuUrl = (tableName: string, menuId: string) => {
  if (tableName === "") return `https://m.orderit.today/menu/${menuId}`;
  return `https://m.orderit.today/menu/${menuId}?t=${tableName}`;
};

const buildPrintCardHtml = (
  name: string,
  qrDataUrl: string,
  location: string
) => `<!DOCTYPE html>
<html>
<head>
  <title>${name || "Menu"} - QR Card</title>
  <style>
    @page { margin: 0; size: A4; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: white;
    }
    .card {
      width: 300px;
      border: 3px solid #1976d2;
      border-radius: 12px;
      overflow: hidden;
      text-align: center;
    }
    .card-header {
      background: #1976d2;
      color: white;
      padding: 18px 16px;
      font-size: 18px;
      font-weight: 700;
    }
    .card-qr { padding: 24px 24px 16px; background: white; }
    .card-qr img { display: block; margin: 0 auto; }
    .card-cta { font-size: 17px; font-weight: 700; color: #1a1a1a; padding: 0 16px 8px; }
    .card-location {
      display: inline-block;
      background: #e3f2fd;
      color: #1976d2;
      font-size: 13px;
      font-weight: 600;
      padding: 4px 12px;
      border-radius: 20px;
      margin-bottom: 10px;
    }
    .card-footer { color: #888; font-size: 12px; padding: 0 16px 20px; }
  </style>
</head>
<body>
  <div class="card">
    <div class="card-header">${name || "Our Menu"}</div>
    <div class="card-qr">
      <img src="${qrDataUrl}" width="220" height="220" alt="Menu QR Code" />
    </div>
    <div class="card-cta">Scan to view our menu</div>
    ${location ? `<div class="card-location">${location}</div>` : ""}
    <div class="card-footer">No app needed · Use your phone camera</div>
  </div>
  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`;

enum QRLabel {
  Base = "Base menu",
  PickUp = "Pick-up",
  Delivery = "Delivery",
  Custom = "Custom",
}
